import { useCallback, useEffect, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { SidebarLayout } from "@productlane/lib/src/sidebar-layout"
import {
	resolveSidebarLayout,
	toStoredSidebarLayout,
} from "@productlane/lib/src/sidebar-layout"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { mutateOk, useAuthData, useZero } from "@/lib/zero"
import { SIDEBAR_LAYOUT_COPY } from "./copy"
import { useSidebarLayoutCache } from "./sidebar-layout-state"

export function useSidebarLayout() {
	const zero = useZero()
	const { workspaceId, userId } = useAuthData()
	const member = `${workspaceId}:${userId}`
	const [user] = useQuery(queries.currentUser())
	const cached = useSidebarLayoutCache((state) => state.byMember[member])
	const remember = useSidebarLayoutCache((state) => state.remember)
	// Undefined until Zero has the member, so the cached layout covers the gap.
	const live = user ? (user.memberships?.sidebarLayout ?? null) : undefined
	const stored = live === undefined ? cached : live
	const layout = useMemo(() => resolveSidebarLayout(stored), [stored])

	useEffect(() => {
		if (live !== undefined) remember(member, live)
	}, [live, member, remember])

	const save = useCallback(
		async (next: SidebarLayout) => {
			try {
				await mutateOk(
					zero.mutate(
						mutators.memberships.setSidebarLayout({
							layout: toStoredSidebarLayout(next),
						}),
					),
				)
			} catch {
				toast.error({ title: SIDEBAR_LAYOUT_COPY.saveFailed })
			}
		},
		[zero],
	)

	return { layout, save }
}
